import React, {Component} from 'react'
import _ from 'lodash'
import {H3, H5, Modal, ModalActions, Button, Row, P, IconButton, Alert} from '../../UI/index.js'
import {formSpacing, FormStack, Toggle, RadioButton} from '../../UI/forms/index.js'
import type {Reseller} from '../../../../server/functions/resellers/resellers.types.js'
import type {ParcelPriceTable} from '../../../../types/types.js'

interface ParcelFee {
    description: string
    price: string
    productCode?: string
    auto: boolean
    applyToAllCarriers: boolean
    carriers: string[]
    applyToAllCountries: boolean
    countries: string[]
    postalCodes?: Record<string, string> // countryCode -> postal code pattern
    conditions: Array<{
        variable: string
        operator: string
        value: number
    }>
    matchAllConditions: boolean
}

interface ImportPriceTableModalProps {
    reseller: Reseller
}

interface ImportPriceTableModalState {
    open: boolean
    fileName: string
    fromReseller: string
    exchangePriceTables: boolean | ''
    overwrite: boolean
    error: string
    priceTables?: Record<string, ParcelPriceTable>
    fees?: ParcelFee[]
    onImport?: (reseller: Reseller) => void
}

export default class ImportPriceTableModal extends Component<ImportPriceTableModalProps, ImportPriceTableModalState> {
    initialState: ImportPriceTableModalState

    constructor(props: ImportPriceTableModalProps) {
        super(props)

        this.initialState = {
            open: false,
            fileName: '',
            fromReseller: '',
            exchangePriceTables: '',
            overwrite: false,
            error: ''
        }

        this.state = structuredClone(this.initialState)
    }

    open(onImport: (reseller: Reseller) => void) {
        this.setState({open: true, onImport})
    }

    close() {
        const input = document.getElementById('table-import') as HTMLInputElement | null
        if (input) {
            input.value = ''
        }
        this.setState(this.initialState)
    }

    onClickImportTable() {
        const input = document.getElementById('table-import') as HTMLInputElement | null
        if (input) {
            input.value = ''
            input.click()
        }
    }

    onLoadFile() {
        this.setState({fromReseller: '', error: ''})
        const input = document.getElementById('table-import') as HTMLInputElement | null
        const file = input?.files?.[0]

        if (file) {
            this.setState({fileName: file.name})
            const reader = new FileReader()
            reader.readAsText(file)
            reader.onerror = (event) => {
                if ((event.target as FileReader)?.error?.name === 'NotReadableError') {
                    alert('Can\'t read file !')
                }
            }
            reader.onload = (event) => {
                try {
                    const res = JSON.parse((event.target as FileReader)?.result as string)
                    if (!res.resellerName || !res.priceTables) {
                        throw new Error('Dit bestand heeft geen geldige opmaak. Er kan alleen een JSON bestand geïmporteerd worden dat ook vanuit Veloyd geëxporteerd is en dat niet is bewerkt.')
                    } else {
                        const fees: ParcelFee[] = (res.fees || []).map((fee: Omit<ParcelFee, 'postalCodes'> & {postalCodes?: string | Record<string, string>}) => {
                            // Files exported before postal codes were set per country contain a single (Dutch) postal code string
                            if (typeof fee.postalCodes === 'string') {
                                return {...fee, postalCodes: fee.postalCodes ? {NL: fee.postalCodes} : {}}
                            }

                            return fee
                        })

                        this.setState({fromReseller: res.resellerName, priceTables: res.priceTables, fees})
                    }
                } catch {
                    this.setState({error: 'Dit bestand heeft geen geldige opmaak. Er kan alleen een JSON bestand geïmporteerd worden dat ook vanuit Veloyd geëxporteerd is en dat niet is bewerkt.'})
                }
            }
        }
    }

    onClickImport() {
        const {fromReseller, overwrite, priceTables, fees, exchangePriceTables, onImport} = this.state
        const {reseller} = this.props

        if (overwrite) {
            reseller.settings.parcels.prices = priceTables || reseller.settings.parcels.prices

            reseller.settings.parcels.fees = reseller.settings.parcels.fees || []
            reseller.settings.parcels.fees = fees || []
        } else {
            Object.keys(priceTables || {}).forEach((priceTableName) => {
                const checkNameAlreadyExists = (name: string): void => {
                    const alreadyExists = !!reseller.settings.parcels.prices[name]
                    if (alreadyExists) {
                        let newName = `${name} (1)`
                        const regex = /\((\d+)\)$/ // Regular expression to match "(x)" at the end of the name
                        const hasNumber = name.match(regex)

                        if (hasNumber) {
                            const number = parseInt(hasNumber[1], 10)
                            const incrementedName = name.replace(regex, `(${number + 1})`)
                            newName = incrementedName
                        }

                        checkNameAlreadyExists(newName)
                    } else {
                        if (priceTables) {
                            priceTables[name] = priceTables[priceTableName]
                            if (name !== priceTableName) {
                                delete priceTables[priceTableName]
                            }
                        }
                    }
                }
                checkNameAlreadyExists(priceTableName)
            })
            _.merge(reseller.settings.parcels.prices, priceTables || {})

            reseller.settings.parcels.fees = reseller.settings.parcels.fees || []
            reseller.settings.parcels.fees.push(...(fees || []))
        }

        if (exchangePriceTables) {
            Object.keys(reseller.settings.parcels.prices).forEach((priceTable) => {
                reseller.settings.parcels.prices[priceTable][reseller.name] = priceTables?.[priceTable]?.[fromReseller] || reseller.settings.parcels.prices[priceTable]?.[reseller.name] || {}
                reseller.settings.parcels.prices[priceTable][fromReseller] = priceTables?.[priceTable]?.[reseller.name] || reseller.settings.parcels.prices[priceTable]?.[fromReseller] || {}
            })
        }

        if (typeof onImport === 'function') {
            onImport(reseller)
        }
        this.close()
    }

    render() {
        const {fileName, fromReseller, open, exchangePriceTables, overwrite, error} = this.state
        const {reseller} = this.props

        return (
            <Modal style={{width: 'fit-content', minWidth: 600, maxWidth: 600}} show={open} onClose={this.close.bind(this)}>
                <Row style={{justifyContent: 'space-between'}}>
                    <H3 style={{marginBottom: 24}}>Importeer tarieftabel</H3>

                    <IconButton onClick={this.close.bind(this)}>
                        <i className='mdi mdi-close' />
                    </IconButton>
                </Row>

                <Button
                    variant='outline-white'
                    icon='mdi mdi-folder-open'
                    label={fileName || 'Selecteer json bestand'}
                    onClick={this.onClickImportTable.bind(this)}
                />

                {fromReseller && (
                    <>
                        <Toggle
                            style={{marginTop: 24, marginBottom: 12}}
                            label='Overschrijf tarieftabellen'
                            info='Wanneer deze optie aan staat dan zullen geïmporteerde tarieftabellen die dezelfde naam hebben als bestaande tarieftabellen deze overschrijven.'
                            checked={overwrite}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({overwrite: event.target.checked})}
                        />

                        {fromReseller !== reseller.name && (
                            <>
                                <H5 style={{marginBottom: 24, marginTop: 24}}>Wissel tarieftabellen</H5>

                                <P>{'Wanneer je tarieven van een andere vestiging importeert kan het handig zijn om de \'eigen vestiging\' tarieven te wisselen met de \'andere vestiging tarieven\'.'}</P>

                                <P style={{marginTop: 12, marginBottom: 12}}>Kies hieronder welke optie van toepassing is:</P>

                                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                                    <RadioButton
                                        label={`Je eigen tarieven voor ${reseller.settings?.accountName || reseller.name} worden de geëxporteerde tarieven van ${fromReseller}`}
                                        checked={exchangePriceTables === true}
                                        unsetWhiteSpace
                                        value={true}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (e.target.value) {
                                                this.setState({exchangePriceTables: true})
                                            }
                                        }}
                                    />

                                    <RadioButton
                                        label={`Je eigen tarieven voor ${reseller.settings?.accountName || reseller.name} worden de geëxporteerde tarieven van ${reseller.settings?.accountName || reseller.name}`}
                                        checked={exchangePriceTables === false}
                                        unsetWhiteSpace
                                        value={false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (!e.target.value) {
                                                this.setState({exchangePriceTables: false})
                                            }
                                        }}
                                    />
                                </FormStack>
                            </>
                        )}
                    </>
                )}

                {error && <Alert style={{marginBottom: 6, marginTop: 24}} variant='error'>{error}</Alert>}

                <ModalActions style={{marginTop: 24}}>
                    <Button variant='text' onClick={this.close.bind(this)}>Annuleren</Button>
                    <Button disabled={(!fromReseller) || (fromReseller !== reseller.name && exchangePriceTables === '')} onClick={this.onClickImport.bind(this)}>Importeer</Button>
                </ModalActions>

                <input
                    type='file'
                    id='table-import'
                    accept='.json'
                    style={{visibility: 'hidden', height: 0}}
                    onChange={this.onLoadFile.bind(this)}
                />
            </Modal>
        )
    }
}
